// src/modules/units/tyres/views/retread-batch/retreadbatchprint.view.js
//
// Pure builder for the printable retread-batch sheets. Data in, one self-contained
// HTML *document* string out — no fetch, no DOM, no app CSS. The orchestrator
// (retreadbatchprint.js) gathers the batch, its resolved supplier and customer
// names, and the full tyre record behind each line, then hands them here; the string
// this returns is written into an isolated <iframe> and printed, so the SPA's chrome
// and stylesheet can never leak into the page and pagination stays the document's
// own concern.
//
// ONE builder, TWO modes — the single Print button adapts to the batch's status:
//   mode 'dispatch' — the consignment sheet sent WITH the casings: # · Tyre ID ·
//                     Serial · RFID · Casing.
//   mode 'received' — the record of what came BACK: the same identity columns plus
//                     Outcome and Cost (excl VAT) per tyre, and a totals row
//                     (tyre count, retreaded/rejected counts, total cost excl VAT).
//                     Lists every line with its final outcome, so it's a complete
//                     record even for a partially-returned batch.
//
// Why a whole document rather than a print stylesheet over the live detail: the
// batch line objects carry only externalTyreId + retread-lifecycle fields (see the
// GET /retread-batch/{id} contract), so serial, RFID and the casing spec are not on
// the screen at all — they're fetched per tyre and assembled here.

import { esc, formatShortDate, formatDateTime, formatMoney } from '../../shared/tyre-format.js';
import { retreadProductBits } from './retreadbatchproduct.view.js';
import { batchStatusMeta, lineStatusMeta } from '../../shared/retread-batch-vocab.js';
import { RETREAD_LINE_STATUS } from '../../shared/retread-batch-core.js';

// Brand palette. The canonical values live in shared/utils/global.js
// (PRIMARY_COLOUR / ACCENT_COLOUR); duplicated here as literals so the print
// document stays self-contained and free of the shared global the tyre module is
// being drawn away from.
const BRAND = '#185785';

const DASH = '—';
const isBlank = (v) => v === null || v === undefined || v === '';
const cell = (v) => (isBlank(v) ? `<span class="muted">${DASH}</span>` : esc(v));

// Join the present parts with a middot; '' when none are present, so an all-empty
// line collapses rather than rendering " · · ".
function joinBits(parts) {
  return parts.map((p) => (isBlank(p) ? '' : String(p).trim())).filter(Boolean).join(' · ');
}

const tyreMake = (t = {}) => t.tyreBrand || t.tyreMake || '';

// Casing identity on one line: Make · Model · Size · Type. Tread pattern and grade
// are intentionally left off. Blanks are dropped; an empty casing renders a dash.
function casingCell(t = {}) {
  const s = joinBits([tyreMake(t), t.tyreModel, t.tyreSize, t.tyreType]);
  return s ? esc(s) : `<span class="muted">${DASH}</span>`;
}

// Rand amount (excl VAT), or a dash. formatMoney groups as "1 234.00" (no symbol).
function money(v) {
  if (isBlank(v)) return `<span class="muted">${DASH}</span>`;
  const n = Number(v);
  return Number.isFinite(n) ? `R ${esc(formatMoney(n))}` : `<span class="muted">${DASH}</span>`;
}

const lineStatusOf = (l = {}) => l.status ?? l.lineStatus ?? null;

// A print-legible colour per outcome (the accent lime is unreadable as text, so the
// received sheet uses a darker green). Labels still come from lineStatusMeta so the
// wording matches the app.
const OUTCOME_COLOUR = {
  [RETREAD_LINE_STATUS.RETURNED_RETREADED]: '#2f9e6f',
  [RETREAD_LINE_STATUS.RETURNED_REJECTED]: '#ad0505',
  [RETREAD_LINE_STATUS.WRITTEN_OFF]: '#64748b',
  [RETREAD_LINE_STATUS.OUT]: '#6b7a8d'
};

// Outcome cell for the received sheet: the status label, coloured, with the reject
// reason on a quiet second line when there is one.
function outcomeCell(line = {}) {
  const status = lineStatusOf(line);
  const label = lineStatusMeta(status).label;
  const colour = OUTCOME_COLOUR[status] || '#6b7a8d';
  const reason = line.rejectReason || line.reason || '';
  const reasonHTML = reason ? `<span class="outcome-reason">${esc(reason)}</span>` : '';
  return `<span class="outcome" style="color:${colour}">${esc(label)}</span>${reasonHTML}`;
}

// One tyre row. `entry.tyre` is the full GET /tyre/{id} record (or the line itself
// when the backend already enriches it, or null on a failed fetch); `entry.line` is
// the batch line (carries the received outcome + cost). The id always comes from the
// line, so it shows even when the tyre record is missing.
function tyreRowHTML(entry, index, received) {
  const t = entry.tyre || {};
  const base =
    `<td class="col-num">${index + 1}</td>` +
    `<td class="col-id">${cell(entry.externalTyreId)}</td>` +
    `<td>${cell(t.tyreSerialNumber)}</td>` +
    `<td>${cell(t.tyreRFID)}</td>` +
    `<td class="col-casing">${casingCell(t)}</td>`;
  const extra = received
    ? `<td class="col-outcome">${outcomeCell(entry.line || {})}</td>` +
      `<td class="col-cost">${money((entry.line || {}).cost)}</td>`
    : '';
  return `<tr>${base}${extra}</tr>`;
}

// Received totals: tyre count, per-outcome counts, and the summed cost (excl VAT).
function computeTotals(list) {
  let retreaded = 0, rejected = 0, writtenOff = 0, cost = 0;
  for (const e of list) {
    const s = lineStatusOf(e.line || {});
    if (s === RETREAD_LINE_STATUS.RETURNED_RETREADED) retreaded++;
    else if (s === RETREAD_LINE_STATUS.RETURNED_REJECTED) rejected++;
    else if (s === RETREAD_LINE_STATUS.WRITTEN_OFF) writtenOff++;
    const c = Number((e.line || {}).cost);
    if (Number.isFinite(c)) cost += c;
  }
  return { total: list.length, retreaded, rejected, writtenOff, cost };
}

// ---- customer identity ----------------------------------------------------------
//
// The customer block comes from GET /customer/{id}: the name, an address, and the
// contact details. The address is the DELIVERY address when the customer has one,
// otherwise the (postal) address — so the retreader always knows where the casings
// go back to. Pure; every part is optional and blanks are dropped.

const trimmed = (v) => (isBlank(v) ? '' : String(v).trim());
const present = (parts) => parts.map(trimmed).filter(Boolean);

/**
 * @param {object|null} customer  GET /customer/{id} record (or null)
 * @param {string} [fallbackName] name to use when the record is missing / nameless
 * @returns {{ name: string, addressLabel: string, addressLines: string[], contactLines: string[] }}
 */
export function customerBits(customer, fallbackName = '') {
  const c = customer && typeof customer === 'object' ? customer : {};
  const name = trimmed(c.name) || trimmed(c.customerName) || trimmed(fallbackName);

  const delivery = present([c.deliveryAddressLine1, c.deliveryAddressLine2, c.deliveryAddressLine3]);
  const postal = present([c.addressLine1, c.addressLine2, c.addressLine3]);
  let addressLabel = '';
  let addressLines = [];
  if (delivery.length) {
    addressLabel = 'Delivery address';
    addressLines = [...delivery, ...present([c.deliveryPostalCode, c.country])];
  } else if (postal.length) {
    addressLabel = 'Address';
    addressLines = [...postal, ...present([c.postalCode, c.country])];
  }

  const phones = present([c.contactTelephoneNumber1, c.contactTelephoneNumber2]);
  const contactLines = present([
    c.contactName,
    trimmed(c.contactCellphoneNumber) ? `Cell ${trimmed(c.contactCellphoneNumber)}` : '',
    phones.length ? `Tel ${phones.join(' / ')}` : '',
    c.contactEmailAddress
  ]);

  return { name, addressLabel, addressLines, contactLines };
}

// Stacked lines inside a meta value: the first (optional) bold, the rest quiet.
function linesHTML(lines, { boldFirst = false } = {}) {
  if (!lines.length) return `<span class="muted">${DASH}</span>`;
  return lines.map((l, i) =>
    (boldFirst && i === 0) ? esc(l) : `<span class="meta-sub">${esc(l)}</span>`
  ).join('<br/>');
}

function metaRow(label, valueHTML) {
  return `
    <div class="meta">
      <span class="meta-label">${esc(label)}</span>
      <span class="meta-value">${valueHTML}</span>
    </div>`;
}

/**
 * Build the full printable document for one batch.
 * @param {object} p
 * @param {object} p.batch           the batch header (+ .lines)
 * @param {string} [p.supplierName]  resolved supplier (retreader) name
 * @param {object} [p.customer]      GET /customer/{id} record (name, address, contact)
 * @param {string} [p.customerName]  fallback customer name when the record is missing
 * @param {Array}  [p.tyres]         [{ externalTyreId, tyre|null, line? }] in line order
 * @param {'dispatch'|'received'} [p.mode]  which sheet to build (default 'dispatch')
 * @param {Date}   [p.printedAt]     defaults to now
 * @returns {string} a complete <!DOCTYPE html> document
 */
export function printDocumentHTML({ batch = {}, supplierName = '', customer = null, customerName = '', tyres = [], mode = 'dispatch', printedAt = new Date() } = {}) {
  const received = mode === 'received';
  const reference = batch.reference || '';
  const statusLabel = batchStatusMeta(batch.status).label;
  const { hasProduct, name: productName, sub: productSub } = retreadProductBits(batch);
  // Product identity only — no numeric spec (nominal / min tread / mileage).
  const product = hasProduct ? joinBits([productName, productSub]) : '';

  const cust = customerBits(customer, customerName);
  const customerHTML = cust.name || cust.addressLines.length
    ? linesHTML([cust.name || DASH, ...cust.addressLines], { boldFirst: true })
    : `<span class="muted">${DASH}</span>`;

  const list = Array.isArray(tyres) ? tyres : [];
  const colCount = received ? 7 : 5;
  const rows = list.length
    ? list.map((e, i) => tyreRowHTML(e, i, received)).join('')
    : `<tr><td class="empty" colspan="${colCount}">No tyres on this batch.</td></tr>`;

  const totals = received ? computeTotals(list) : null;
  const tfoot = received && list.length
    ? `<tfoot>
        <tr class="totals">
          <td colspan="6" class="totals-label">Totals · ${totals.total} tyre${totals.total === 1 ? '' : 's'} · ${totals.retreaded} retreaded · ${totals.rejected} rejected${totals.writtenOff ? ` · ${totals.writtenOff} written off` : ''}</td>
          <td class="col-cost totals-cost">R ${esc(formatMoney(totals.cost))}</td>
        </tr>
      </tfoot>`
    : '';

  const headRow = received
    ? `<tr><th class="col-num">#</th><th>Tyre ID</th><th>Serial number</th><th>RFID</th><th>Casing</th><th>Outcome</th><th class="col-cost">Cost (excl VAT)</th></tr>`
    : `<tr><th class="col-num">#</th><th>Tyre ID</th><th>Serial number</th><th>RFID</th><th>Casing</th></tr>`;

  const docKind = received ? 'Retread — received' : 'Retread consignment';
  const tyresTitle = received ? 'Tyres received' : 'Tyres on this batch';
  const title = `${received ? 'Retread received' : 'Retread batch'} ${reference}`.trim();
  const signLeft = received ? 'Received by / date' : 'Dispatched by / date';
  const signRight = received ? 'Checked by / date' : 'Received by / date';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${esc(title)}</title>
<style>
  @page { size: A4 portrait; margin: 16mm 14mm 18mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font: 12px/1.45 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
    color: #1c2733;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .head {
    display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
    border-bottom: 3px solid ${BRAND}; padding-bottom: 12px;
  }
  .brand { display: flex; align-items: center; gap: 10px; }
  .brand-name { font-size: 16px; font-weight: 700; color: ${BRAND}; letter-spacing: .3px; }
  .doc-kind { font-size: 11px; text-transform: uppercase; letter-spacing: 1.4px; color: #6b7a8d; }
  .head-right { text-align: right; }
  .ref-label { font-size: 10px; text-transform: uppercase; letter-spacing: 1.2px; color: #6b7a8d; }
  .ref { font-size: 22px; font-weight: 700; color: ${BRAND}; }
  .status {
    display: inline-block; margin-top: 4px; font-size: 10.5px; font-weight: 700;
    text-transform: uppercase; letter-spacing: .6px; color: ${BRAND};
    border: 1px solid ${BRAND}; border-radius: 999px; padding: 2px 9px;
  }

  .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 28px; margin: 16px 0 6px; }
  .meta { display: flex; flex-direction: column; gap: 1px; }
  .meta-label { font-size: 9.5px; text-transform: uppercase; letter-spacing: .8px; color: #6b7a8d; }
  .meta-value { font-size: 12.5px; font-weight: 600; color: #1c2733; }
  .meta-sub { font-weight: 400; font-size: 12px; color: #3a4a5b; }

  .tyres-title {
    margin: 18px 0 6px; font-size: 12px; font-weight: 700; color: ${BRAND};
    text-transform: uppercase; letter-spacing: .8px;
  }
  table { width: 100%; border-collapse: collapse; }
  thead { display: table-header-group; }        /* repeat the header on each printed page */
  th {
    text-align: left; font-size: 9.5px; text-transform: uppercase; letter-spacing: .6px;
    color: #55677a; border-bottom: 2px solid ${BRAND}; padding: 7px 8px; background: #f3f7fb;
  }
  td { padding: 7px 8px; border-bottom: 1px solid #dde5ee; vertical-align: top; }
  tr { break-inside: avoid; }
  tbody tr:nth-child(even) td { background: #f8fbfd; }
  .col-num { width: 26px; color: #8a99a8; text-align: right; }
  .col-id { font-weight: 700; }
  .col-casing { width: 26%; }
  .col-outcome { white-space: nowrap; }
  .col-cost { text-align: right; white-space: nowrap; }
  .outcome { font-weight: 700; }
  .outcome-reason { display: block; color: #6b7a8d; font-weight: 400; font-size: 11px; }
  .muted { color: #9aa7b4; }
  .empty { text-align: center; color: #8a99a8; padding: 18px 8px; }

  tfoot .totals td { border-top: 2px solid ${BRAND}; border-bottom: none; font-weight: 700; padding-top: 9px; }
  .totals-label { text-align: right; color: #1c2733; }
  .totals-cost { text-align: right; color: ${BRAND}; }

  .foot { margin-top: 22px; display: flex; justify-content: space-between; align-items: flex-end; gap: 20px; }
  .signs { display: flex; gap: 28px; }
  .sign { min-width: 190px; }
  .sign-line {
    border-top: 1px solid #9aa7b4; margin-top: 26px; padding-top: 3px;
    font-size: 10px; color: #6b7a8d; text-transform: uppercase; letter-spacing: .6px;
  }
  .printed { font-size: 9.5px; color: #8a99a8; text-align: right; }
</style>
</head>
<body>
  <div class="head">
    <div class="brand">
      <span>
        <span class="brand-name">PressureTrack</span><br/>
        <span class="doc-kind">${esc(docKind)}</span>
      </span>
    </div>
    <div class="head-right">
      <div class="ref-label">Batch no</div>
      <div class="ref">${cell(reference)}</div>
      ${statusLabel ? `<div class="status">${esc(statusLabel)}</div>` : ''}
    </div>
  </div>

  <div class="meta-grid">
    ${metaRow(cust.addressLabel ? `Customer · ${cust.addressLabel.toLowerCase()}` : 'Customer', customerHTML)}
    ${metaRow('Supplier (retreader)', cell(supplierName))}
    ${metaRow('Customer contact', linesHTML(cust.contactLines, { boldFirst: true }))}
    ${metaRow('Retread product', product ? esc(product) : `<span class="muted">${DASH}</span>`)}
    ${metaRow('Tyres in batch', String(list.length))}
    ${metaRow('Dispatched', batch.dispatchedAt ? esc(formatShortDate(batch.dispatchedAt)) : `<span class="muted">${DASH}</span>`)}
    ${metaRow('Expected return', batch.expectedReturnDate ? esc(formatShortDate(batch.expectedReturnDate)) : `<span class="muted">${DASH}</span>`)}
    ${batch.dispatchReference ? metaRow('Dispatch note', esc(batch.dispatchReference)) : ''}
  </div>

  <div class="tyres-title">${esc(tyresTitle)}</div>
  <table>
    <thead>${headRow}</thead>
    <tbody>${rows}</tbody>
    ${tfoot}
  </table>

  <div class="foot">
    <div class="signs">
      <div class="sign"><div class="sign-line">${esc(signLeft)}</div></div>
      <div class="sign"><div class="sign-line">${esc(signRight)}</div></div>
    </div>
    <div class="printed">Printed ${esc(formatDateTime(printedAt))}</div>
  </div>
</body>
</html>`;
}

// ---- email ------------------------------------------------------------------------
//
// The same sheet, for an email. There is no mail-sending endpoint, so the app opens
// the user's mail client (mailto:) with a subject and a plain-text body, and puts an
// inline-styled HTML copy of the sheet on the clipboard to paste in for a formatted
// version. Both come from one plain-value model so they can't drift from each other.

// Plain (unescaped) values for one tyre row, in column order.
function rowValues(entry, received) {
  const t = entry.tyre || {};
  const vals = [
    trimmed(entry.externalTyreId),
    trimmed(t.tyreSerialNumber),
    trimmed(t.tyreRFID),
    joinBits([tyreMake(t), t.tyreModel, t.tyreSize, t.tyreType])
  ];
  if (received) {
    const line = entry.line || {};
    const reason = trimmed(line.rejectReason || line.reason);
    vals.push(lineStatusMeta(lineStatusOf(line)).label + (reason ? ` (${reason})` : ''));
    const n = Number(line.cost);
    vals.push(!isBlank(line.cost) && Number.isFinite(n) ? `R ${formatMoney(n)}` : '');
  }
  return vals;
}

function emailModel({ batch = {}, supplierName = '', customer = null, customerName = '', tyres = [], mode = 'dispatch' } = {}) {
  const received = mode === 'received';
  const list = Array.isArray(tyres) ? tyres : [];
  const { hasProduct, name: productName, sub: productSub } = retreadProductBits(batch);
  const totals = received ? computeTotals(list) : null;
  return {
    received,
    reference: trimmed(batch.reference),
    docKind: received ? 'Retread — received' : 'Retread consignment',
    statusLabel: batchStatusMeta(batch.status).label || '',
    cust: customerBits(customer, customerName),
    facts: [
      ['Supplier (retreader)', trimmed(supplierName)],
      ['Retread product', hasProduct ? joinBits([productName, productSub]) : ''],
      ['Tyres in batch', String(list.length)],
      ['Dispatched', batch.dispatchedAt ? formatShortDate(batch.dispatchedAt) : ''],
      ['Expected return', batch.expectedReturnDate ? formatShortDate(batch.expectedReturnDate) : ''],
      ['Dispatch note', trimmed(batch.dispatchReference)]
    ],
    tyresTitle: received ? 'Tyres received' : 'Tyres on this batch',
    headers: received
      ? ['#', 'Tyre ID', 'Serial number', 'RFID', 'Casing', 'Outcome', 'Cost (excl VAT)']
      : ['#', 'Tyre ID', 'Serial number', 'RFID', 'Casing'],
    rows: list.map((e, i) => [String(i + 1), ...rowValues(e, received)]),
    totalsLine: totals && list.length
      ? `Totals · ${totals.total} tyre${totals.total === 1 ? '' : 's'} · ${totals.retreaded} retreaded · ${totals.rejected} rejected` +
        `${totals.writtenOff ? ` · ${totals.writtenOff} written off` : ''} · R ${formatMoney(totals.cost)} (excl VAT)`
      : ''
  };
}

/** Subject line, e.g. "Retread consignment RT-2026-00027". */
export function emailSubject({ batch = {}, mode = 'dispatch' } = {}) {
  const kind = mode === 'received' ? 'Retread batch received' : 'Retread consignment';
  return `${kind} ${trimmed(batch.reference)}`.trim();
}

/**
 * Plain-text body. With `{ includeTyres: false }` the tyre list is replaced by a
 * pointer to the clipboard copy — used when the full body would be too long for a
 * mailto: link.
 */
export function emailText(p = {}, { includeTyres = true } = {}) {
  const m = emailModel(p);
  const out = [];
  out.push(`${m.docKind} — batch ${m.reference || DASH}${m.statusLabel ? ` (${m.statusLabel})` : ''}`);
  out.push('');
  out.push(`Customer: ${m.cust.name || DASH}`);
  if (m.cust.addressLines.length) out.push(`${m.cust.addressLabel}: ${m.cust.addressLines.join(', ')}`);
  if (m.cust.contactLines.length) out.push(`Contact: ${m.cust.contactLines.join(' · ')}`);
  for (const [label, value] of m.facts) {
    if (value || label === 'Supplier (retreader)') out.push(`${label}: ${value || DASH}`);
  }
  out.push('');
  if (!includeTyres) {
    out.push(`${m.tyresTitle}: ${m.rows.length} — the full list is on your clipboard; paste it here (Ctrl+V).`);
    if (m.totalsLine) out.push(m.totalsLine);
    return out.join('\n');
  }
  out.push(`${m.tyresTitle}:`);
  if (!m.rows.length) out.push('No tyres on this batch.');
  for (const r of m.rows) {
    const [num, ...rest] = r;
    out.push(`${num}. ${rest.map((v) => v || DASH).join(' | ')}`);
  }
  if (m.totalsLine) { out.push(''); out.push(m.totalsLine); }
  return out.join('\n');
}

/**
 * An email-safe HTML fragment of the sheet: inline styles only (mail clients drop
 * <style> blocks and classes on paste), a plain table, no scripts or images.
 */
export function emailHTML(p = {}) {
  const m = emailModel(p);
  const td = 'padding:6px 8px;border-bottom:1px solid #dde5ee;vertical-align:top;font-size:13px;';
  const th = `padding:6px 8px;border-bottom:2px solid ${BRAND};background:#f3f7fb;text-align:left;font-size:11px;text-transform:uppercase;color:#55677a;`;
  const label = 'padding:3px 16px 3px 0;color:#6b7a8d;font-size:12px;vertical-align:top;white-space:nowrap;';
  const value = 'padding:3px 0;color:#1c2733;font-size:13px;vertical-align:top;';
  const v = (s) => (s ? esc(s) : DASH);

  const facts = [
    ['Customer', [m.cust.name, ...m.cust.addressLines].filter(Boolean).map(esc).join('<br>') || DASH],
    ['Customer contact', m.cust.contactLines.map(esc).join('<br>') || DASH],
    ...m.facts.filter(([l, val]) => val || l === 'Supplier (retreader)').map(([l, val]) => [l, v(val)])
  ].map(([l, html]) => `<tr><td style="${label}">${esc(l)}</td><td style="${value}">${html}</td></tr>`).join('');

  const head = m.headers.map((h) => `<th style="${th}">${esc(h)}</th>`).join('');
  const body = m.rows.length
    ? m.rows.map((r) => `<tr>${r.map((c) => `<td style="${td}">${v(c)}</td>`).join('')}</tr>`).join('')
    : `<tr><td style="${td}" colspan="${m.headers.length}">No tyres on this batch.</td></tr>`;
  const totals = m.totalsLine
    ? `<p style="margin:10px 0 0;font-weight:700;font-size:13px;color:#1c2733;">${esc(m.totalsLine)}</p>`
    : '';

  return `<div style="font-family:Segoe UI,Roboto,Arial,sans-serif;color:#1c2733;">` +
    `<p style="margin:0 0 2px;font-size:12px;text-transform:uppercase;letter-spacing:1px;color:#6b7a8d;">${esc(m.docKind)}</p>` +
    `<p style="margin:0 0 12px;font-size:20px;font-weight:700;color:${BRAND};">${v(m.reference)}` +
      `${m.statusLabel ? ` <span style="font-size:12px;font-weight:600;">(${esc(m.statusLabel)})</span>` : ''}</p>` +
    `<table style="border-collapse:collapse;margin:0 0 14px;">${facts}</table>` +
    `<p style="margin:0 0 6px;font-size:12px;font-weight:700;text-transform:uppercase;color:${BRAND};">${esc(m.tyresTitle)}</p>` +
    `<table style="border-collapse:collapse;width:100%;"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>` +
    totals +
    `</div>`;
}
